import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Comment } from '@models/Sessions/Idea/Comment';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { BusService } from '@services/bus.service';
import { ConfigService, IInputLengths } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { Gpt3Service } from '@services/gpt3/gpt3.service';
// import { HighlightTag } from 'angular-text-input-highlight';
import _ from 'lodash';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'comment-list',
  templateUrl: './comment-list.component.html',
  styleUrls: ['./comment-list.component.scss'],
})
export class CommentListComponent
  implements OnInit, OnDestroy, AfterViewChecked, OnChanges
{
  @ViewChild('commentlist', { static: true }) commentlist: ElementRef;
  @ViewChild('textarea', { static: true }) textarea: ElementRef;

  @Input() standalone = false;
  @Input() session: Session;
  @Input() idea: Idea;
  @Input() draft: any;
  @Output() draftSave = new EventEmitter<any>();

  comment: any = {};
  inputLengths: IInputLengths;

  mentionConfig = {
    items: [],
    labelKey: 'mention',
  };

  mentionHighlights: any[] = [];

  pageHeight: number;
  focusedComment: Comment;
  focusedForReply = false;
  initialScrollTimeout: any;
  awaitedComment: Comment = undefined;
  feedbackGenerationInProgress: boolean;

  constructor(
    private config: ConfigService,
    private bus: BusService,
    private events: EventsService,
    private gpt3service: Gpt3Service,
  ) {
    this.inputLengths = config.inputLengths;
  }

  ngOnInit() {
    this.prepareMentions();
    this.initialScrollTimeout = setTimeout(() => this.scrollToBottom(), 200);

    this.bus.subscribe(
      this.events.received.action.session.idea.addComment.success,
      this.receiveComment,
      this,
    );
  }

  ngOnDestroy() {
    this.bus.unsubscribe(
      this.events.received.action.session.idea.addComment.success,
      this.receiveComment,
    );
  }

  ngAfterViewChecked() {
    this.adoptHeight();
    if (this.draft) {
      this.comment.text = this.draft.text;
      if (this.draft.parent) {
        this.comment.parent = this.idea.comments.find(
          c => c._id.toString() === this.draft.parent,
        );
      }
    }
  }

  ngOnChanges() {
    this.adoptHeight();
    if (
      this.awaitedComment &&
      this.idea.comments.some(
        c => c._id.toString() === this.awaitedComment._id.toString(),
      )
    ) {
      this.focusComment(this.awaitedComment);
      this.awaitedComment = undefined;
    }
  }

  get rootComments() {
    return this.idea.comments.filter(comment => !comment.parent_id);
  }

  private scrollToBottom() {
    this.commentlist.nativeElement.scroll({
      top: this.commentlist.nativeElement.scrollHeight,
      behavior: 'smooth',
    });
  }

  adoptHeight() {
    this.pageHeight = parseInt(
      window.getComputedStyle(this.commentlist.nativeElement).height,
      10,
    );
  }

  get scrollable(): boolean {
    return this.commentlist.nativeElement.scrollTop > 0;
  }

  scrollOnePageUp() {
    this.commentlist.nativeElement.scrollBy({
      top: -this.pageHeight * 0.9,
      behavior: 'smooth',
    });
  }

  scrollToComment(element: ElementRef) {
    clearTimeout(this.initialScrollTimeout);

    let scrollOffset = 64;
    if (this.session.config.benefits_and_challenges) {
      scrollOffset = 128;
    }

    setTimeout(() => {
      this.commentlist.nativeElement.scroll({
        top: element.nativeElement.offsetTop - scrollOffset,
        behavior: 'smooth',
      });
    }, 200);
  }

  focusComment(comment: Comment) {
    if (this.focusedComment) {
      this.focusedComment = undefined;
      setTimeout(() => this.focusComment(comment), 1);
    } else {
      this.focusedComment = comment;
      this._showParentReplies(comment);
    }
  }

  receiveComment(event) {
    if (event.response) {
      const comment = event.response;
      if (
        this.idea.comments.some(
          c => c._id.toString() === comment._id.toString(),
        )
      ) {
        this.focusComment(comment);
      } else {
        this.awaitedComment = comment;
      }
    }
  }

  get participantDict() {
    return _.keyBy(this.session.config.participants, '_id');
  }

  postComment() {
    this.comment.mentions = this.mentionHighlights.map(highlight => ({
      user_id: highlight.data._id,
      text: '@' + highlight.data.mention,
    }));

    if (this.comment.parent) {
      this.comment.parent_id = this.comment.parent._id;
    }

    this.bus.publish(
      this.events.requested.action.session.idea.addComment,
      this.comment,
      this.idea,
      this.session,
    );
    this.bus.publish(
      this.events.requested.action.tracking.track,
      this.session,
      'comment',
      'create',
      this.comment.text,
    );

    this.comment = {};
    this.mentionHighlights = [];
    this.emitDraft();
  }

  generateFeedback() {
    this.feedbackGenerationInProgress = true;
    this.gpt3service
      .generateFeedback(
        this.session.config.one_liner,
        this.session.config.title,
        this.idea.title,
        this.idea.description,
      )
      .subscribe(
        response => {
          this.comment = { text: response };
          this.feedbackGenerationInProgress = false;
        },
        () => {
          this.feedbackGenerationInProgress = false;
          alert(
            'An error occured while generating feedback. Please try again later',
          );
        },
      );
  }

  emitDraft() {
    this.draftSave.emit(
      this.comment
        ? {
            text: this.comment.text,
            parent: this.comment.parent ? this.comment.parent._id : undefined,
          }
        : {},
    );
  }

  reply(comment: Comment) {
    this.comment.parent = comment;
    this.focusComment(comment);
    this.focusedForReply = true;
    this.textarea.nativeElement.focus();
    this.emitDraft();
  }

  cancelReply() {
    this.comment.parent = undefined;
    if (this.focusedForReply) {
      this.focusedComment = undefined;
      this.focusedForReply = false;
    }
    this.emitDraft();
  }

  prepareMentions() {
    this.mentionConfig.items = this.session.config.participants
      .map(participant => {
        if (
          participant.user &&
          (participant.user.name || participant.user.first_name)
        ) {
          let mention = participant.user.name || participant.user.first_name;
          if (participant.user.first_name) {
            mention = participant.user.first_name + ' ' + mention;
          }
          if (participant.user.organization) {
            mention = mention + ` (${participant.user.organization})`;
          }
          return Object.assign(
            { mention: mention.replace(/\s/g, '_') },
            participant.user,
          );
        }
        return null;
      })
      .filter(__ => __);
  }

  highlightMentions() {
    this.mentionHighlights = [];

    const match = /(@[\w|(|)]+) ?/g;
    let word;

    while ((word = match.exec(this.comment.text))) {
      let p;
      if (
        (p = this.mentionConfig.items.find(
          _p => '@' + _p.mention === word[0].trim(),
        ))
      ) {
        this.mentionHighlights.push({
          indices: {
            start: word.index,
            end: word.index + p.mention.length + 1,
          },
          data: p,
          cssClass: 'mentioned-user',
        });
      }
    }
  }

  private _showParentReplies(comment: Comment) {
    if (comment.parent_id) {
      const parent = this.idea.comments.find(
        c => c._id.toString() === comment.parent_id.toString(),
      );
      if (parent) {
        (parent as any).hideReplies = false;
        this._showParentReplies(parent);
      }
    }
  }
}
